import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CategoryPicker,
  Hairline,
  Tab,
  TabPanel,
  Tabs,
  TabsList,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import { AdjustStockModal } from "../components/AdjustStockModal.js";
import { ProductDetailsFields } from "../components/ProductDetailsFields.js";
import { TranslationEditor } from "../components/TranslationEditor.js";
import { VariantEditModal } from "../components/VariantEditModal.js";
import { VolumeBreaksEditor } from "./VolumeBreaksEditor.js";
import type {
  AllergenCode,
  CategoryTreeNode,
  DietaryTagCode,
  ProductDetailDTO,
  ProductDTO,
  StorageType,
  VariantDTO,
  ImageDTO,
} from "@wmart/shared-types";

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ProductEdit() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === undefined;
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();

  // --- General fields ---
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [vatCategory, setVatCategory] = useState<string>("food_zero");
  const [status, setStatus] = useState<"draft" | "active" | "archived">("draft");

  // --- Spec fields ---
  const [brandId, setBrandId] = useState<string | null>(null);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [originCountry, setOriginCountry] = useState<string | null>(null);
  const [allergens, setAllergens] = useState<AllergenCode[]>([]);
  const [dietaryTags, setDietaryTags] = useState<DietaryTagCode[]>([]);
  const [ingredients, setIngredients] = useState("");
  const [specExtra, setSpecExtra] = useState<Record<string, string>>({});

  // --- Storage fields (stored as strings so inputs work naturally) ---
  const [storageType, setStorageType] = useState<StorageType | null>(null);
  const [storageTempMinC, setStorageTempMinC] = useState("");
  const [storageTempMaxC, setStorageTempMaxC] = useState("");
  const [shelfLifeDaysUnopened, setShelfLifeDaysUnopened] = useState("");
  const [shelfLifeDaysOpened, setShelfLifeDaysOpened] = useState("");
  const [storageNotes, setStorageNotes] = useState("");

  // --- Children (only populated in edit mode) ---
  const [variants, setVariants] = useState<VariantDTO[]>([]);
  const [images, setImages] = useState<ImageDTO[]>([]);
  const [assignedCategoryIds, setAssignedCategoryIds] = useState<string[]>([]);
  const [tree, setTree] = useState<CategoryTreeNode[]>([]);
  const [vbEditorVariantId, setVbEditorVariantId] = useState<string | null>(null);
  const [adjustVariant, setAdjustVariant] = useState<VariantDTO | null>(null);
  const [variantModal, setVariantModal] = useState<{ variant: VariantDTO | null } | null>(null);
  const [busy, setBusy] = useState(false);

  // --- Helpers ---

  function numOrNull(s: string): number | null {
    const n = parseInt(s, 10);
    return s === "" || isNaN(n) ? null : n;
  }

  async function loadAll() {
    if (isNew) return;
    try {
      const [product, treeRes] = await Promise.all([
        api.get<ProductDetailDTO>(`/v1/admin/products/${id}`),
        api.get<{ tree: CategoryTreeNode[] }>("/v1/admin/categories"),
      ]);
      // General
      setSlug(product.slug);
      setTitle(product.title);
      setDescription(product.description ?? "");
      setVatCategory(product.vat_category_code);
      setStatus(product.status);
      // Spec
      setBrandId(product.brand_id ?? null);
      setSupplierId(product.supplier_id ?? null);
      setOriginCountry(product.origin_country);
      setAllergens(product.allergens);
      setDietaryTags(product.dietary_tags);
      setIngredients(product.ingredients ?? "");
      setSpecExtra(product.spec_extra);
      // Storage
      setStorageType(product.storage_type);
      setStorageTempMinC(product.storage_temp_min_c?.toString() ?? "");
      setStorageTempMaxC(product.storage_temp_max_c?.toString() ?? "");
      setShelfLifeDaysUnopened(product.shelf_life_days_unopened?.toString() ?? "");
      setShelfLifeDaysOpened(product.shelf_life_days_opened?.toString() ?? "");
      setStorageNotes(product.storage_notes ?? "");
      // Children
      setVariants(product.variants);
      setImages(product.images);
      setAssignedCategoryIds(product.category_ids);
      setTree(treeRes.tree);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load product");
    }
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // In create mode also load category tree so the Categories section works
  useEffect(() => {
    if (!isNew) return;
    api
      .get<{ tree: CategoryTreeNode[] }>("/v1/admin/categories")
      .then((res) => setTree(res.tree))
      .catch(() => {/* non-critical */});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveProduct(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const specStoragePayload = {
        brand_id: brandId,
        supplier_id: supplierId,
        origin_country: originCountry,
        allergens,
        dietary_tags: dietaryTags,
        ingredients: ingredients || null,
        spec_extra: specExtra,
        storage_type: storageType,
        storage_temp_min_c: numOrNull(storageTempMinC),
        storage_temp_max_c: numOrNull(storageTempMaxC),
        shelf_life_days_unopened: numOrNull(shelfLifeDaysUnopened),
        shelf_life_days_opened: numOrNull(shelfLifeDaysOpened),
        storage_notes: storageNotes || null,
      };

      if (isNew) {
        const created = await api.post<ProductDTO>("/v1/admin/products", {
          slug,
          title,
          description: description || undefined,
          vat_category_code: vatCategory,
          status,
          category_ids: assignedCategoryIds,
          ...specStoragePayload,
        });
        notify("success", "Product created");
        navigate(`/products/${created.id}`);
      } else {
        await api.patch(`/v1/admin/products/${id}`, {
          slug,
          title,
          description: description || null,
          vat_category_code: vatCategory,
          status,
          ...specStoragePayload,
        });
        notify("success", "Product saved");
        void loadAll();
      }
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Save failed");
    } finally {
      setBusy(false);
    }
  }

  function addVariant() {
    if (isNew) {
      notify("error", "Save the product first before adding variants");
      return;
    }
    setVariantModal({ variant: null });
  }

  async function deleteVariant(variantId: string) {
    if (!confirm("Soft-delete this variant?")) return;
    try {
      await api.delete(`/v1/admin/products/${id}/variants/${variantId}`);
      notify("success", "Variant deleted");
      void loadAll();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
    }
  }

  async function uploadImage(file: File) {
    if (isNew) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.upload(`/v1/admin/products/${id}/images`, fd);
      notify("success", "Image uploaded");
      void loadAll();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Upload failed");
    }
  }

  async function deleteImage(imageId: string) {
    if (!confirm("Soft-delete this image?")) return;
    try {
      await api.delete(`/v1/admin/products/${id}/images/${imageId}`);
      notify("success", "Image deleted");
      void loadAll();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
    }
  }

  async function syncCategoryAssignments(nextIds: string[]) {
    if (isNew) {
      setAssignedCategoryIds(nextIds);
      return;
    }
    const toAdd = nextIds.filter((cid) => !assignedCategoryIds.includes(cid));
    const toRemove = assignedCategoryIds.filter((cid) => !nextIds.includes(cid));
    try {
      await Promise.all([
        ...toAdd.map((cid) =>
          api.post(`/v1/admin/products/${id}/categories`, { category_id: cid }),
        ),
        ...toRemove.map((cid) =>
          api.delete(`/v1/admin/products/${id}/categories/${cid}`),
        ),
      ]);
      setAssignedCategoryIds(nextIds);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Category sync failed");
      void loadAll();
    }
  }

  const detailsFields = {
    slug, setSlug,
    title, setTitle,
    description, setDescription,
    vatCategory, setVatCategory,
    status, setStatus,
    brandId, setBrandId,
    supplierId, setSupplierId,
    originCountry, setOriginCountry,
    allergens, setAllergens,
    dietaryTags, setDietaryTags,
    ingredients, setIngredients,
    specExtra, setSpecExtra,
    storageType, setStorageType,
    storageTempMinC, setStorageTempMinC,
    storageTempMaxC, setStorageTempMaxC,
    shelfLifeDaysUnopened, setShelfLifeDaysUnopened,
    shelfLifeDaysOpened, setShelfLifeDaysOpened,
    storageNotes, setStorageNotes,
  };

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <h1 className="font-serif text-display-sm text-ink-900">
        {isNew ? "New product" : title || "Product"}
      </h1>

      {isNew ? (
        // Create mode: Details card + Categories card, no Tabs
        <div className="flex flex-col gap-6">
          <Card className="p-6">
            <form onSubmit={saveProduct} className="flex flex-col gap-4">
              <ProductDetailsFields {...detailsFields} />
              <Hairline />
              <div>
                <Button type="submit" disabled={busy} loading={busy}>
                  Create
                </Button>
              </div>
            </form>
          </Card>

          <Card className="p-6">
            <h2 className="mb-4 font-serif text-title-lg text-ink-900">
              Categories
            </h2>
            <CategoryPicker
              tree={tree}
              selected={assignedCategoryIds}
              onChange={syncCategoryAssignments}
              mode="multi"
            />
          </Card>
        </div>
      ) : (
        <Tabs defaultValue="details">
          <TabsList aria-label="Product detail sections">
            <Tab value="details">Details</Tab>
            <Tab value="variants">Variants</Tab>
            <Tab value="categories">Categories</Tab>
            <Tab value="images">Images</Tab>
            <Tab value="translations">Translations</Tab>
          </TabsList>

          {/* ---- Details tab ---- */}
          <TabPanel value="details">
            <Card className="p-6">
              <form onSubmit={saveProduct} className="flex flex-col gap-4">
                <ProductDetailsFields {...detailsFields} />
                <Hairline />
                <div>
                  <Button type="submit" disabled={busy} loading={busy}>
                    Save
                  </Button>
                </div>
              </form>
            </Card>
          </TabPanel>

          {/* ---- Variants tab ---- */}
          <TabPanel value="variants">
            <Card as="section" className="p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-title-lg text-ink-900">
                  Variants
                </h2>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => addVariant()}
                >
                  + Add variant
                </Button>
              </div>
              <div className="mt-4">
                {variants.length === 0 ? (
                  <p className="py-4 text-center text-body-sm text-ink-500">
                    No variants yet.
                  </p>
                ) : (
                  <Table>
                    <THead>
                      <TR>
                        <TH>SKU</TH>
                        <TH>Title</TH>
                        <TH className="text-right">Base price (minor)</TH>
                        <TH className="text-right">Stock</TH>
                        <TH className="text-right">Actions</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {variants.map((v) => (
                        <TR key={v.id}>
                          <TD className="font-mono text-body-sm">{v.sku}</TD>
                          <TD>{v.title}</TD>
                          <TD className="text-right">
                            {v.base_price_minor} {v.currency}
                          </TD>
                          <TD className="text-right">{v.stock_quantity}</TD>
                          <TD className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => setVariantModal({ variant: v })}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => setVbEditorVariantId(v.id)}
                              >
                                Volume breaks
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => setAdjustVariant(v)}
                              >
                                Adjust stock
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => void deleteVariant(v.id)}
                                className="text-danger"
                              >
                                Delete
                              </Button>
                            </div>
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                )}
              </div>
            </Card>
          </TabPanel>

          {/* ---- Categories tab ---- */}
          <TabPanel value="categories">
            <Card as="section" className="p-6">
              <h2 className="mb-4 font-serif text-title-lg text-ink-900">
                Categories
              </h2>
              <CategoryPicker
                tree={tree}
                selected={assignedCategoryIds}
                onChange={syncCategoryAssignments}
                mode="multi"
              />
            </Card>
          </TabPanel>

          {/* ---- Images tab ---- */}
          <TabPanel value="images">
            <Card as="section" className="p-6">
              <h2 className="font-serif text-title-lg text-ink-900">Images</h2>
              <div className="mt-4 flex flex-col gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void uploadImage(f);
                    e.target.value = "";
                  }}
                  className="text-body-sm text-ink-700 file:mr-3 file:rounded-sm file:border file:border-ink-200 file:bg-white file:px-3 file:py-1.5 file:text-body-sm hover:file:bg-accent-soft"
                />
                <div className="flex flex-wrap gap-3">
                  {images.map((img) => (
                    <div key={img.id} className="relative">
                      <img
                        src={`/v1/admin/images/${img.gcs_object_key}`}
                        alt={img.alt ?? ""}
                        className="h-32 w-32 rounded-sm border border-ink-200 object-cover"
                      />
                      <button
                        type="button"
                        aria-label="Delete image"
                        onClick={() => void deleteImage(img.id)}
                        className="absolute right-1 top-1 rounded-sm bg-danger px-2 py-0.5 text-caption text-ivory focus-visible:shadow-focus outline-none"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {images.length === 0 ? (
                    <p className="text-body-sm text-ink-500">No images yet.</p>
                  ) : null}
                </div>
              </div>
            </Card>
          </TabPanel>

          {/* ---- Translations tab ---- */}
          <TabPanel value="translations">
            {id && <TranslationEditor kind="product" id={id} />}
          </TabPanel>
        </Tabs>
      )}

      {vbEditorVariantId ? (
        <VolumeBreaksEditor
          productId={id!}
          variantId={vbEditorVariantId}
          onClose={() => {
            setVbEditorVariantId(null);
            void loadAll();
          }}
        />
      ) : null}

      {adjustVariant ? (
        <AdjustStockModal
          variantId={adjustVariant.id}
          variantSku={adjustVariant.sku}
          currentStock={adjustVariant.stock_quantity}
          onSuccess={() => {
            void loadAll();
          }}
          onClose={() => setAdjustVariant(null)}
        />
      ) : null}

      {variantModal ? (
        <VariantEditModal
          productId={id!}
          variant={variantModal.variant}
          onClose={() => setVariantModal(null)}
          onSaved={() => {
            setVariantModal(null);
            void loadAll();
          }}
        />
      ) : null}
    </div>
  );
}
